import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { storage } from '../utils/storage'
import { Platform } from '../utils/platform'
import { supabase } from '../services/supabase'
import { useProfileStore } from './useProfileStore'

// Get the appropriate storage implementation
const getStorage = () => {
  if (Platform.isNative && (global as any).__FLOWS_STORAGE__) {
    return (global as any).__FLOWS_STORAGE__;
  }
  return storage;
};

export interface TransactionColumn {
  id: string
  label: string
  visible: boolean
  width?: number
  sortable?: boolean
  filterable?: boolean
}

export interface DateTimeFormat {
  date: string
  time: string
  timezone: string
}

export interface DashboardWidget {
  id: string
  name: string
  enabled: boolean
  order: number
  settings?: Record<string, any>
}

// Define the user preferences structure to match the database schema
export interface UserPreferences {
  id?: string;
  user_id: string;
  theme: string;
  language: string;
  currency: string;
  date_format: string;
  time_format: string;
  number_format: string;
  timezone: string;
  notifications_enabled: boolean;
  created_at?: string;
  updated_at?: string;
  // Additional fields for app-specific preferences (stored as JSON in the database)
  preferences?: {
    transactionColumns?: Record<string, TransactionColumn>;
    columnOrder?: string[];
    dateTimeFormat?: DateTimeFormat;
    exchangeRates?: {
      rates: Record<string, number>;
      lastUpdated?: string;
    };
    start_of_week?: number;
    start_of_month?: number;
    fiscal_year_start?: number;
    dashboardWidgets?: DashboardWidget[];
  };
}

const defaultColumns: Record<string, TransactionColumn> = {
  date: { id: 'date', label: 'Date', visible: true, width: 120, sortable: true },
  description: { id: 'description', label: 'Description', visible: true, width: 200, filterable: true },
  category: { id: 'category', label: 'Category', visible: true, width: 150, filterable: true },
  amount: { id: 'amount', label: 'Amount', visible: true, width: 120, sortable: true },
  account: { id: 'account', label: 'Account', visible: true, width: 150, filterable: true },
  payee: { id: 'payee', label: 'Payee', visible: true, width: 150, filterable: true },
  status: { id: 'status', label: 'Status', visible: true, width: 100, filterable: true },
  notes: { id: 'notes', label: 'Notes', visible: false, width: 200, filterable: true }
}

const defaultColumnOrder = ['date', 'description', 'category', 'amount', 'account', 'payee', 'status', 'notes']

const defaultDateTimeFormat: DateTimeFormat = {
  date: 'YYYY-MM-DD',
  time: 'HH:mm:ss',
  timezone: 'UTC'
}

const defaultDashboardWidgets: DashboardWidget[] = [
  { id: 'balance-card', name: 'Balance Card', enabled: true, order: 0 },
  { id: 'monthly-overview', name: 'Monthly Overview', enabled: true, order: 1 },
  { id: 'recent-transactions', name: 'Recent Transactions', enabled: true, order: 2, settings: { limit: 3 } },
  { id: 'budget-progress', name: 'Budget Progress', enabled: true, order: 3, settings: { limit: 3 } },
  { id: 'saving-goals', name: 'Saving Goals', enabled: false, order: 4, settings: { limit: 2 } },
  { id: 'quick-actions', name: 'Quick Actions', enabled: false, order: 5 },
  { id: 'financial-insights', name: 'Financial Insights', enabled: false, order: 6 }
]

const DEFAULT_USER_PREFERENCES: UserPreferences = {
  user_id: '',
  theme: 'light',
  language: 'en',
  currency: 'USD',
  date_format: 'MMM d, yyyy',
  time_format: 'HH:mm',
  number_format: '#,##0.00',
  timezone: 'UTC',
  notifications_enabled: true,
  preferences: {
    transactionColumns: defaultColumns,
    columnOrder: defaultColumnOrder,
    dateTimeFormat: defaultDateTimeFormat,
    exchangeRates: {
      rates: {},
    },
    start_of_week: 1,
    start_of_month: 1,
    fiscal_year_start: 1,
    dashboardWidgets: defaultDashboardWidgets
  }
}

interface SettingsState {
  settings: UserPreferences | null
  isLoading: boolean
  error: string | null
  fetchSettings: (userId: string) => Promise<void>
  updateSettings: (userId: string, newSettings: Partial<UserPreferences>) => Promise<void>
  updateTransactionColumns: (userId: string, columns: Record<string, TransactionColumn>) => Promise<void>
  updateColumnOrder: (userId: string, order: string[]) => Promise<void>
  updateDateTimeFormat: (userId: string, format: DateTimeFormat) => Promise<void>
  updateDashboardWidgets: (userId: string, widgets: DashboardWidget[]) => Promise<void>
  resetSettings: () => void
}

/**
 * Handle common database errors related to settings operations
 * @param error The error object to handle
 * @returns A user-friendly error message
 */
const handleDatabaseError = (error: any): string => {
  // Handle RLS policy violations
  if (error.code === '42501') {
    console.error(`
      Row Level Security (RLS) policy violation detected for user_preferences table.
      
      This likely means one of the following:
      1. Your user doesn't have permission to insert/update the user_preferences table
      2. The user_preferences table has RLS policies that restrict operations
      
      To fix this:
      - Check the RLS policies in your Supabase dashboard for the user_preferences table
      - Ensure your user has the necessary permissions
    `)
    return 'Permission denied: You do not have access to update preferences'
  } 
  // Handle column not found errors
  else if (error.code === 'PGRST204') {
    console.error(`
      Column error detected: ${error.message}
      
      This likely means the database schema doesn't match what the code expects.
      Check if the 'user_id' column exists in the user_preferences table.
      
      To fix this:
      - Run ALTER TABLE user_preferences ADD COLUMN user_id UUID REFERENCES auth.users(id);
      - Update RLS policies to use user_id instead of id
    `)
    return `Database schema error: ${error.message}`
  }
  
  // Handle other errors
  return error instanceof Error ? error.message : 'An unknown error occurred'
}

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set, get) => ({
      settings: null,
      isLoading: false,
      error: null,

      resetSettings: () => {
        console.log('Resetting settings to defaults')
        set({ 
          settings: null,
          isLoading: false,
          error: null
        })
      },

      fetchSettings: async (userId: string) => {
        if (!userId) {
          set({ error: 'User ID is required to fetch settings' });
          return;
        }

        set({ isLoading: true, error: null });
        
        try {
          console.log('Fetching settings for user:', userId);
          
          // Query the user_preferences table
          const { data, error } = await supabase
            .from('user_preferences')
            .select('*')
            .eq('user_id', userId)
            .single();

          if (error) {
            throw error;
          }

          if (data) {
            console.log('Found user preferences:', data);
            
            // Parse the preferences JSON field if it exists
            let preferences = {};
            if (data.preferences) {
              try {
                if (typeof data.preferences === 'string') {
                  preferences = JSON.parse(data.preferences);
                } else {
                  preferences = data.preferences;
                }
              } catch (e) {
                console.error('Error parsing preferences JSON:', e);
              }
            }

            // Merge with default preferences to ensure all fields exist
            const mergedPreferences = {
              ...DEFAULT_USER_PREFERENCES.preferences,
              ...preferences
            };

            // Set the settings with the merged data
            set({ 
              settings: {
                ...data,
                preferences: mergedPreferences
              } as UserPreferences,
              isLoading: false 
            });
          } else {
            console.log('No preferences found, creating default preferences');
            
            // If no preferences found, create default preferences using userId as id
            const defaultPrefs = {
              ...DEFAULT_USER_PREFERENCES,
              id: userId, // Use userId instead of generating a new UUID
              user_id: userId,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            };
            
            console.log('Inserting default preferences:', defaultPrefs);
            
            // Insert default preferences
            const { error: insertError } = await supabase
              .from('user_preferences')
              .insert(defaultPrefs);
              
            if (insertError) {
              throw insertError;
            }
            
            set({ 
              settings: defaultPrefs,
              isLoading: false 
            });
          }
        } catch (error: any) {
          console.error('Error fetching settings:', error);
          const errorMessage = handleDatabaseError(error);
          set({ 
            error: errorMessage,
            isLoading: false 
          });
        }
      },

      updateSettings: async (userId: string, newSettings: Partial<UserPreferences>) => {
        set({ isLoading: true, error: null });
        try {
          // Validate user ID
          if (!userId) {
            throw new Error('User ID is required to update settings');
          }

          const currentState = get();
          const currentSettings = currentState.settings;
          
          // First check if a record exists for this user
          const { data: existingSettings, error: fetchError } = await supabase
            .from('user_preferences')
            .select('id')
            .eq('user_id', userId)
            .single();
          
          if (fetchError && fetchError.code !== 'PGRST116') { // PGRST116 is "not found"
            throw fetchError;
          }
          
          // Always use userId as the ID if it exists, otherwise create a new ID
          const id = existingSettings?.id || userId;
          
          // Prepare update data
          const updateData: Record<string, any> = {
            id,
            user_id: userId,
            updated_at: new Date().toISOString()
          };
          
          // Handle flat fields
          const flatFields = [
            'theme', 'language', 'currency', 
            'date_format', 'time_format', 'number_format', 
            'timezone', 'notifications_enabled'
          ];
          
          flatFields.forEach(field => {
            if (field in newSettings) {
              updateData[field] = newSettings[field as keyof UserPreferences];
            }
          });
          
          // Handle nested preferences
          if (newSettings.preferences) {
            // Get current preferences or default
            const currentPreferences = currentSettings?.preferences || {};
            
            // Merge with new preferences
            updateData.preferences = {
              ...currentPreferences,
              ...newSettings.preferences
            };
          }
          
          // Update the user_preferences table
          const { error } = await supabase
            .from('user_preferences')
            .upsert(updateData)
            .select()
            .single();

          if (error) {
            throw error;
          }
          
          // Update local state
          set((state) => ({
            settings: {
              ...state.settings || DEFAULT_USER_PREFERENCES,
              ...newSettings,
              preferences: updateData.preferences || state.settings?.preferences
            },
            isLoading: false,
            error: null
          }));
          
          // Also update the ProfileStore to sync the settings
          // This ensures that components using ProfileStore can see the changes
          const profileStore = useProfileStore.getState();
          const currentProfileSettings = profileStore.settings;
          
          if (currentProfileSettings) {
            // Create a properly structured update for profile preferences
            const profileUpdate = {
              ...currentProfileSettings,
              preferences: {
                ...currentProfileSettings.preferences,
              }
            };
            
            // Update the format preferences specifically
            if (newSettings.date_format) {
              profileUpdate.preferences.date_format = newSettings.date_format;
            }
            if (newSettings.time_format) {
              profileUpdate.preferences.time_format = newSettings.time_format;
            }
            if (newSettings.number_format) {
              profileUpdate.preferences.number_format = newSettings.number_format;
            }
            
            // Update the profile store
            profileStore.setSettings(profileUpdate);
          }
        } catch (error) {
          console.error('Error updating settings:', error);
          set({
            error: handleDatabaseError(error),
            isLoading: false
          });
          throw error;
        }
      },

      updateTransactionColumns: async (userId: string, columns: Record<string, TransactionColumn>) => {
        set({ isLoading: true, error: null });
        try {
          // Validate user ID
          if (!userId) {
            throw new Error('User ID is required to update transaction columns');
          }

          const currentState = get();
          const currentSettings = currentState.settings;

          if (!currentSettings) {
            throw new Error('Settings not found. Please fetch settings first.');
          }
          
          // Use the correct ID from current settings
          const id = currentSettings.id;
          
          // Ensure preferences object exists
          const currentPreferences = currentSettings.preferences || {};
          
          // Update the preferences with new columns
          const updatedPreferences = {
            ...currentPreferences,
            transactionColumns: columns
          };
          
          // Prepare update data
          const updateData = {
            id,
            user_id: userId,
            preferences: updatedPreferences,
            updated_at: new Date().toISOString()
          };
          
          // Update the user_preferences table
          const { error } = await supabase
            .from('user_preferences')
            .update(updateData)
            .eq('id', id);

          if (error) {
            throw error;
          }

          // Update local state
          set((state) => ({
            settings: {
              ...state.settings!,
              preferences: updatedPreferences,
              updated_at: updateData.updated_at
            },
            isLoading: false,
            error: null
          }));
        } catch (error) {
          console.error('Error updating transaction columns:', error);
          set({
            error: handleDatabaseError(error),
            isLoading: false
          });
          throw error;
        }
      },

      updateColumnOrder: async (userId: string, order: string[]) => {
        set({ isLoading: true, error: null });
        try {
          // Validate user ID
          if (!userId) {
            throw new Error('User ID is required to update column order');
          }

          const currentState = get();
          const currentSettings = currentState.settings;

          if (!currentSettings) {
            throw new Error('Settings not found. Please fetch settings first.');
          }
          
          // Use the correct ID from current settings
          const id = currentSettings.id;
          
          // Ensure preferences object exists
          const currentPreferences = currentSettings.preferences || {};
          
          // Update the preferences with new column order
          const updatedPreferences = {
            ...currentPreferences,
            columnOrder: order
          };
          
          // Prepare update data
          const updateData = {
            id,
            user_id: userId,
            preferences: updatedPreferences,
            updated_at: new Date().toISOString()
          };
          
          // Update the user_preferences table
          const { error } = await supabase
            .from('user_preferences')
            .update(updateData)
            .eq('id', id);

          if (error) {
            throw error;
          }

          // Update local state
          set((state) => ({
            settings: {
              ...state.settings!,
              preferences: updatedPreferences,
              updated_at: updateData.updated_at
            },
            isLoading: false,
            error: null
          }));
        } catch (error) {
          console.error('Error updating column order:', error);
          set({
            error: handleDatabaseError(error),
            isLoading: false
          });
          throw error;
        }
      },

      updateDateTimeFormat: async (userId: string, format: DateTimeFormat) => {
        set({ isLoading: true, error: null });
        try {
          // Validate user ID
          if (!userId) {
            throw new Error('User ID is required to update date/time format');
          }

          const currentState = get();
          const currentSettings = currentState.settings;

          if (!currentSettings) {
            throw new Error('Settings not found. Please fetch settings first.');
          }
          
          // Use the correct ID from current settings
          const id = currentSettings.id;
          
          // Ensure preferences object exists
          const currentPreferences = currentSettings.preferences || {};
          
          // Update the preferences with new date/time format
          const updatedPreferences = {
            ...currentPreferences,
            dateTimeFormat: format
          };
          
          // Prepare update data
          const updateData = {
            id,
            user_id: userId,
            preferences: updatedPreferences,
            updated_at: new Date().toISOString()
          };
          
          // Update the user_preferences table
          const { error } = await supabase
            .from('user_preferences')
            .update(updateData)
            .eq('id', id);

          if (error) {
            throw error;
          }

          // Update local state
          set((state) => ({
            settings: {
              ...state.settings!,
              preferences: updatedPreferences,
              updated_at: updateData.updated_at
            },
            isLoading: false,
            error: null
          }));
        } catch (error) {
          console.error('Error updating date/time format:', error);
          set({
            error: handleDatabaseError(error),
            isLoading: false
          });
          throw error;
        }
      },

      updateDashboardWidgets: async (userId: string, widgets: DashboardWidget[]) => {
        set({ isLoading: true, error: null });
        try {
          // Validate user ID
          if (!userId) {
            throw new Error('User ID is required to update dashboard widgets');
          }

          const currentState = get();
          const currentSettings = currentState.settings;

          if (!currentSettings) {
            throw new Error('Settings not found. Please fetch settings first.');
          }
          
          // Use the correct ID from current settings
          const id = currentSettings.id;
          
          // Ensure preferences object exists
          const currentPreferences = currentSettings.preferences || {};
          
          // Update the preferences with new widgets
          const updatedPreferences = {
            ...currentPreferences,
            dashboardWidgets: widgets
          };
          
          // Prepare update data
          const updateData = {
            id,
            user_id: userId,
            preferences: updatedPreferences,
            updated_at: new Date().toISOString()
          };
          
          // Update the user_preferences table
          const { error } = await supabase
            .from('user_preferences')
            .update(updateData)
            .eq('id', id);

          if (error) {
            throw error;
          }

          // Update local state
          set((state) => ({
            settings: {
              ...state.settings!,
              preferences: updatedPreferences,
              updated_at: updateData.updated_at
            },
            isLoading: false,
            error: null
          }));
        } catch (error) {
          console.error('Error updating dashboard widgets:', error);
          set({
            error: handleDatabaseError(error),
            isLoading: false
          });
          throw error;
        }
      }
    }),
    {
      name: 'flows-settings',
      storage: createJSONStorage(() => getStorage()),
    }
  )
) 